import React from "react";

import { BaseAPI } from "api/base_api";
import { ClassMonitoringInfoType, PageUnloadReasonType } from "shared/types";
import { ClassEventType } from "shared/types/class_event";
import { MonitoringEventType } from "shared/types/monitoring_event";
import { redirectToHome } from "utils/classroom";

const MAX_RETRIES = 3;

export class FlightAPI extends BaseAPI {
  /*
   * Provides a monotonic clock synchronized to the server.
   *
   * This is used for soft time synchronization: for example, if a user
   * expresses an emotion that should be rendered for a limited period of time,
   * we will generally model that by recording the timestamp of when it
   * started. Clients can then independently play an animation based on the
   * start time and their notion of shared server time from this module.
   */

  private localStartTime: number | null;

  private serverStartTime: number | null;

  private serverRtt: number | null;

  userIsAlreadyPresent: boolean;

  constructor(baseUrl: string, userId: string) {
    super(baseUrl, userId);
    this.userIsAlreadyPresent = false;
    this.localStartTime = null;
    this.serverStartTime = null;
    this.serverRtt = null;
  }

  // eslint-disable-next-line class-methods-use-this
  log(...args: unknown[]) {
    FlightAPI.log(...args);
  }

  // eslint-disable-next-line class-methods-use-this
  warn(...args: unknown[]) {
    FlightAPI.warn(...args);
  }

  // eslint-disable-next-line class-methods-use-this
  error(...args: unknown[]) {
    FlightAPI.error(...args);
  }

  // eslint-disable-next-line class-methods-use-this
  getHeaders() {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
  }

  requestErrorCallback(
    url: string,
    method: "POST" | "GET",
    data: string | FormData,
    status: number | string,
  ) {
    if (status === 403) {
      try {
        const parsedUrl = new URL(url);
        if (parsedUrl.pathname.endsWith("/api/monitoring")) {
          this.error(`User ${this.userId} is not permitted to monitor classes`);
          redirectToHome(PageUnloadReasonType.NOT_PERMITTED_TO_MONITOR_CLASS);
        }
      } catch {
        this.error(`Failed to parse URL ${url} for user ${this.userId}`);
      }
    }
  }

  async start(classId: string) {
    try {
      this.log("Starting Flight API");
      // TODO: Periodically calibrate against the server, but preserve monotonicity here.
      const serverRequestStart = performance.now();
      const response = await this.get("api/serverTime");
      if (!response.ok) {
        throw new Error("Failed to get server time");
      }
      this.serverStartTime = await response.json();
      const serverRequestEnd = performance.now();
      this.serverRtt = serverRequestEnd - serverRequestStart;
      this.localStartTime = performance.now();
      // The timeout is so this log occurs after LoggingWrapper is rendered so it can be sent to Datadog
      setTimeout(() => {
        this.log(
          `Flight API started 5s ago with serverStartTime=${this.serverStartTime}, serverRtt=${this.serverRtt}, localStartTime=${this.localStartTime}`,
        );
      }, 5000);

      await Promise.all([this.getPresence(classId), this.getFlags()]);
    } catch {
      // If we failed to contact the server, try again in one second.
      setTimeout(this.start.bind(this), 1000);
    }
  }

  async getPresence(classId: string) {
    const response = await this.get(`api/get-presence?userId=${this.userId}&classId=${classId}`);
    if (response.ok) {
      this.userIsAlreadyPresent = (await response.json()) as boolean;
    }
  }

  getServerMilliseconds() {
    if (this.serverStartTime === null || this.serverRtt === null || this.localStartTime === null) {
      return 0;
    }
    return this.serverStartTime + performance.now() - this.localStartTime + this.serverRtt / 2.0;
  }

  async getFlags() {
    const response = await this.get(`api/flags`);
    if (response.ok) {
      window.FLAGS = await response.json();
    }
  }

  async getMonitoringData(dateString: string): Promise<ClassMonitoringInfoType[]> {
    const response = await this.get(`api/monitoring${dateString ? `?date=${dateString}` : ""}`);
    if (!response.ok) {
      return [];
    }
    return response.json() as Promise<ClassMonitoringInfoType[]>;
  }

  async postClassEvent(classId: string, payload: Omit<ClassEventType, "id" | "timestamp">) {
    if (!this.baseUrl) {
      this.warn("baseUrl is not defined");
      return;
    }
    const { type, userId, roomId, data } = payload;
    const body = JSON.stringify({
      classId,
      type,
      userId,
      roomId,
      data,
    });
    return this.post(`api/class-events`, body, false, MAX_RETRIES);
  }

  async postMonitoringEvent(classId: string, payload: Partial<MonitoringEventType>) {
    if (!this.baseUrl) {
      this.warn("baseUrl is not defined");
      return;
    }
    const { type, userId, orgId, orgShortName, classType, contextData } = payload;
    const data = JSON.stringify({
      classId,
      type,
      userId,
      orgId,
      orgShortName,
      classType,
      contextData: JSON.stringify(contextData),
    });
    return this.post(`api/monitoring-events`, data, false, MAX_RETRIES);
  }
}

export const FlightAPIContext = React.createContext(new FlightAPI("", ""));
